import {
    Component,
    computed,
    ElementRef,
    EventEmitter,
    Output,
    Signal,
    ViewChild,
} from '@angular/core'
import {
    API_ERROR_FEATURE_DISABLED,
    API_ERROR_INSUFFICIENT_SESSIONS_FOR_FEATURE,
    BookSessionPreferences,
    BookSessionsResponse,
    MeetingsBookingsPairedMeeting,
    ConfirmedSessionsResponse,
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    User,
} from '@app/core/_models'
import {
    BookSessionInfo,
    BookSessionsService,
    MeetingsService,
    CancelSessionService,
    UtilsService,
} from '@app/core/_services'
import {
    AnalyticsService,
    BookingSource,
    SessionTileSource,
} from '@app/core/_services/analytics.service'
import { DeviceDetectionService } from '@app/core/_services/device-detection.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { ErrorHandlerService } from '@app/core/_services/error-handler.service'
import { PlayToneService } from '@app/core/_services/play-tone.service'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import { SESSION_STATE_SEARCHING } from '@app/core/_services/session-state.service'
import { UserService } from '@app/core/_services/user.service'

import { filter, finalize, firstValueFrom, forkJoin, Subject, take, takeUntil } from 'rxjs'

type ActiveSessionState = 'none' | 'searching' | 'matched'

const USER_PROP_HAS_TRIED_NOW_SESSIONS = 'hasTriedNowSessions'
import { ButtonModule } from 'primeng/button'

@Component({
    selector: 'fm-now-sessions-sidebar',
    templateUrl: './now-sessions-sidebar.component.html',
    styleUrl: './now-sessions-sidebar.component.scss',
    imports: [ButtonModule],
})
export class NowSessionsSidebarComponent {
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    @Output() onBookingCompleted: EventEmitter<void> = new EventEmitter<void>()

    @ViewChild('startSessionButton') startSessionButton: ElementRef

    private user: User

    public firstTimeViewing: boolean = false

    public bookingSession: boolean = false
    public activeSessionState: ActiveSessionState = 'none'
    public activeSession: ConfirmedSessionsResponse | MeetingsBookingsPairedMeeting | null = null
    private hasRegisteredForSessionUpdates: boolean = false
    public sessionLengthStr: Signal<string> = computed(() => {
        // hardcodeing session duration to 50 minutes until we decide to allow now sessions of other lengths
        //const sessionDuration = this.sessionSettingsService.sessionDurationSetting()
        const sessionDuration = SESSION_DURATION_50_MINS
        if (sessionDuration === SESSION_DURATION_25_MINS) {
            return '25'
        } else if (sessionDuration === SESSION_DURATION_50_MINS) {
            return '50'
        } else {
            return '75'
        }
    })

    public buttonText = computed(() => {
        return `Start ${this.sessionLengthStr()} min session`
    })

    constructor(
        private sessionSettingsService: SessionSettingsService,
        private bookSessionsService: BookSessionsService,
        private cancelSessionService: CancelSessionService,
        private userService: UserService,
        private dialogService: DialogService,
        private analyticsService: AnalyticsService,
        private playToneService: PlayToneService,
        private deviceDetectionService: DeviceDetectionService,
        private errorHandlerService: ErrorHandlerService,
        private meetingsService: MeetingsService,
        private utilsService: UtilsService,
    ) {}

    ngOnInit() {
        this.userService.currentUser.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user) => {
            this.user = user

            if (this.user.userId && !this.hasRegisteredForSessionUpdates) {
                this.hasRegisteredForSessionUpdates = true
                this.registerForBookingsUpdates()
            }
        })

        this.bookSessionsService.bookNowSessionClickedSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.onClickStartSessionButton()
            })
    }

    async onClickStartSessionButton(): Promise<void> {
        this.analyticsService.logClickedEvent(this.buttonText(), 'Now Sessions Sidebar')

        this.playToneService.playEndSessionToneMuted()

        if (!this.deviceDetectionService.isSupportedBrowserDaily()) {
            this.dialogService.openSafariWarning()
            return
        }

        // display modal if first time using now sessions
        if (!this.user.properties[USER_PROP_HAS_TRIED_NOW_SESSIONS]) {
            this.dialogService.openNowSessionsFirstTimeUseModal(async (resp: boolean) => {
                this.user.properties[USER_PROP_HAS_TRIED_NOW_SESSIONS] = true
                this.userService.update(this.user).subscribe()

                if (!resp) {
                    return
                }

                const booked = await this.bookSessionWrapper()
                if (booked) {
                    this.onBookingCompleted.emit()
                }
            })
            return
        } else {
            const booked = await this.bookSessionWrapper()
            if (booked) {
                this.onBookingCompleted.emit()
            }
        }
    }

    private async bookSessionWrapper(): Promise<boolean> {
        this.bookingSession = true
        try {
            return await this.bookAndJoinSession()
        } finally {
            this.bookingSession = false
        }
    }

    private async bookAndJoinSession(): Promise<boolean> {
        // hardcoding session duration to 50 minutes until we decide to allow now sessions of other lengths
        //const sessionDuration = this.sessionSettingsService.sessionDurationSetting()
        const sessionDuration = SESSION_DURATION_50_MINS
        const favorites = this.sessionSettingsService.getSessionSettingFavoritesPreference()
        const quietMode = this.sessionSettingsService.quietModeEnabled()
        const activityType = this.sessionSettingsService.getSessionSettingActivityType()

        const preferences: BookSessionPreferences = {
            favorites: { value: favorites },
            quietMode: { value: quietMode },
        }

        let bookSessionInfo: BookSessionInfo = {
            sessionTime: -1,
            title: null,
            duration: sessionDuration,
            preferences,
            activityType,
            userAvailabilityCode: null,
            userId: null,
        }

        try {
            const response = await firstValueFrom(
                this.bookSessionsService.bookSessions(
                    [bookSessionInfo],
                    this.user.timeZone,
                    BookingSource.NowSessionsSidebar,
                ),
            )

            const parsedRes = this.bookSessionsService.parseBookResponse(response)

            if (parsedRes.numSuccess === 1) {
                this.bookSessionsService.joinSession(response[0].sessionTime)
                return true
            }

            this.handleBookFailure(response, parsedRes)
            return false
        } catch (error) {
            this.errorHandlerService.handleErrorResponse(error)
            return false
        }
    }

    private handleBookFailure(
        response: BookSessionsResponse[],
        parsedRes: ReturnType<BookSessionsService['parseBookResponse']>,
    ): void {
        if (parsedRes.planLimitReached) {
            this.dialogService.openPlanLimitReachedDialog()
            return
        }

        if (parsedRes.sessionConflict) {
            this.dialogService.openSessionConflictWithCancel(
                parsedRes.conflicts,
                this.user.timeZone,
                (actionResult) => {
                    if (actionResult?.action === 'cancelAndBook') {
                        this.bookingSession = true
                        const cancelRequests = parsedRes.conflicts.map((conflict) => {
                            return this.cancelSessionService.cancelSession(
                                conflict.externalId,
                                SessionTileSource.NowSessionsSidebar,
                            )
                        })

                        forkJoin(cancelRequests)
                            .pipe(finalize(() => (this.bookingSession = false)))
                            .subscribe({
                                next: () => this.bookAndJoinSession(),
                                error: (e) => {
                                    this.errorHandlerService.handleErrorResponse(e)
                                },
                            })
                    }
                },
            )
            return
        }

        if (parsedRes.inviterNoLongerAvailable) {
            this.dialogService.openInviterMeetingNoLongerAvailable()
            return
        }

        const bookError = response[0]?.error
        if (bookError?.error_code === API_ERROR_FEATURE_DISABLED) {
            this.dialogService.openFeatureNotAvailableModal()
            return
        }

        if (bookError?.error_code === API_ERROR_INSUFFICIENT_SESSIONS_FOR_FEATURE) {
            this.errorHandlerService.showErrorToast('You have not unlocked this feature yet.')
            return
        }

        this.errorHandlerService.logUnhandledError(bookError ?? response, {
            source: 'now-sessions-sidebar.book',
        })
    }

    private registerForBookingsUpdates(): void {
        // search for
        // - upcoming session in searching state
        // - currently ongoing session (or now session with a meeting room that has not ended)
        this.meetingsService.meetingsBookingsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((eventsBookingsState) => {
                this.activeSession = null
                this.activeSessionState = 'none'
                for (const meeting of eventsBookingsState.meetings) {
                    if (meeting.meetingType !== 'paired') {
                        continue
                    }
                    const session = meeting
                    const now = this.utilsService.getCurrentTimestamp()

                    // searching sessions
                    if (session.state === SESSION_STATE_SEARCHING) {
                        this.activeSession = session
                        this.activeSessionState = 'searching'
                        break
                    } else if (
                        session.sessionType === 'now' &&
                        !this.utilsService.hasSessionEnded(
                            session.startMs,
                            session.durationMs,
                            now,
                        ) &&
                        session.meetingRoom
                    ) {
                        // ongoing matched now session
                        this.activeSession = session
                        this.activeSessionState = 'matched'
                    } else if (
                        session.sessionType === 'standard' &&
                        session.meetingRoom &&
                        this.utilsService.ongoingSession(session.startMs, session.durationMs, now)
                    ) {
                        // ongoing matched regular session
                        this.activeSession = session
                        this.activeSessionState = 'matched'
                    }
                }
            })
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
